import { Injectable, signal } from '@angular/core';

/** Child Form chrome reported to the outer Manage tab strip and save status. */
export interface FormDefinitionTabState {
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly errorCount: number;
}

const emptyState: FormDefinitionTabState = { dirty: false, saving: false, errorCount: 0 };

/**
 * Host-scoped store for formDefinition tab child Form chrome.
 * Child definitions register via `setup`; Manage host reads for dirty dots and save status.
 */
@Injectable()
export class FormDefinitionTabStateStore {

  private readonly $states = signal(new Map<string, FormDefinitionTabState>());

  public set(tabPath: string, state: FormDefinitionTabState): void {

    this.$states.update(current => {
      const next = new Map(current);
      next.set(tabPath, state);
      return next;
    });
  }

  public clear(tabPath: string): void {

    this.$states.update(current => {
      if (!current.has(tabPath)) return current;
      const next = new Map(current);
      next.delete(tabPath);
      return next;
    });
  }

  public get(tabPath: string): FormDefinitionTabState {

    return this.$states().get(tabPath) ?? emptyState;
  }

  public anyDirty(): boolean {

    for (const state of this.$states().values()) {
      if (state.dirty) return true;
    }
    return false;
  }

  public anySaving(): boolean {

    for (const state of this.$states().values()) {
      if (state.saving) return true;
    }
    return false;
  }

  public anyFailed(): boolean {

    for (const state of this.$states().values()) {
      if (state.errorCount > 0) return true;
    }
    return false;
  }
}
